/**
 * @file ProductTaxonomySection.tsx
 * @description Вид, роль и публикация товара (phase14.0.2; phase19.22 — W22).
 *
 * Две независимые оси с разными именами (решение пользователя 2026-09-25):
 *   - «Роль в производстве» — `itemKind`, фиксированный список (было «Тип
 *     товара»): что это для склада и производства;
 *   - «Вид товара» — справочник W22 (`productKindId`, пусто — как в категории):
 *     срок годности, этикетка, НДС.
 * Под видом — «Срок годности по умолчанию, дней» (W20): только если
 * действующий вид требует срок. Смена вида на «срок не нужен» прячет поле с
 * тостом, значение в форме остаётся. Ниже — tristate публикации на сайте.
 */

import { Layers } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import { ProductKindField } from "../../../../components/catalog/product-kinds/ProductKindField";
import { ShelfLifeDaysField } from "../../../../components/catalog/product-kinds/ShelfLifeDaysField";
import { Label } from "../../../../components/ui/label";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { SelectField } from "../../../../components/ui/SelectField";
import type { CatalogCategory } from "../../../../lib/api-catalog";
import type { ProductKind } from "../../../../lib/api-product-kinds";
import { effectivePublishOnSiteForCategory } from "../../../../lib/catalog-category-tree";
import { resolveCategoryKind, resolveProductKind } from "../../../../lib/product-kind-resolve";
import { ITEM_KIND_OPTIONS, type ProductFormState } from "../product-form";
import { PublishOnSiteRadios } from "./PublishOnSiteRadios";
import { SectionCard } from "./SectionCard";

const ROLE_OPTIONS = [{ value: "", label: "Не задана" }, ...ITEM_KIND_OPTIONS];

interface Props {
  form: ProductFormState;
  categories: CatalogCategory[];
  kinds: ProductKind[];
  onChange: (patch: Partial<ProductFormState>) => void;
  /** Новый товар — вид обязателен (свой или от категории). */
  isNew: boolean;
  disabled?: boolean;
}

export function ProductTaxonomySection({
  form,
  categories,
  kinds,
  onChange,
  isNew,
  disabled,
}: Props) {
  const notify = useNotify();
  const catById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const kindsById = useMemo(() => new Map(kinds.map((k) => [k.id, k])), [kinds]);

  const categoryKind = resolveCategoryKind(form.categoryId, catById, kindsById);
  const effective = resolveProductKind(form, catById, kindsById);
  // null — вид неизвестен (не задан/справочник не загружен): поле срока не прячем.
  const requiresExpiry = effective.kind ? effective.kind.requiresExpiry : null;

  // Вид сменился на «срок не нужен» — поле прячется; предупреждаем, что
  // введённое значение не потеряно (оно уйдёт при сохранении как раньше).
  const prevRequires = useRef<boolean | null>(requiresExpiry);
  useEffect(() => {
    if (prevRequires.current === true && requiresExpiry === false && form.shelfLifeDays) {
      notify.info(
        `Вид «${effective.kind?.name ?? ""}» не требует срока годности — поле скрыто, значение сохранено.`,
      );
    }
    prevRequires.current = requiresExpiry;
  }, [requiresExpiry, form.shelfLifeDays, effective.kind?.name, notify]);

  const inheritedPublish = useMemo(() => {
    if (!form.categoryId) return null;
    return effectivePublishOnSiteForCategory(form.categoryId, catById);
  }, [form.categoryId, catById]);

  return (
    <SectionCard icon={<Layers />} title="Вид товара и публикация">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="p-item-kind">Роль в производстве</Label>
          <SelectField
            id="p-item-kind"
            ariaLabel="Роль в производстве"
            value={form.itemKind}
            onChange={(v) => onChange({ itemKind: v as ProductFormState["itemKind"] })}
            options={ROLE_OPTIONS}
            disabled={disabled}
          />
          <p className="type-caption text-on-surface-variant">
            Что это для склада и производства: остаток, вход и выход тех.карты.
          </p>
        </div>

        <ProductKindField
          value={form.productKindId}
          onChange={(productKindId) => onChange({ productKindId })}
          kinds={kinds}
          categoryKind={categoryKind}
          required={isNew}
          disabled={disabled}
        />

        {requiresExpiry !== false ? (
          <ShelfLifeDaysField
            value={form.shelfLifeDays}
            onChange={(shelfLifeDays) => onChange({ shelfLifeDays })}
            kind={effective.kind}
            disabled={disabled}
          />
        ) : null}

        <PublishOnSiteRadios
          value={form.isPublishedOnSite}
          inherited={inheritedPublish}
          onChange={(isPublishedOnSite) => onChange({ isPublishedOnSite })}
          disabled={disabled}
        />
      </div>
    </SectionCard>
  );
}
